import { cloneDeep } from 'lodash';

import AuthNFallbackPolicyApi from 'client/api/v2/AuthNFallbackPolicyApi';
import AuthNFallbackPolicyType from 'client/Security/AuthNFallbackPolicyType';
import createModel from 'client/store/modules/models/createModel';

export const GlobalAuthPolicyConsts = {
  userPortal: 'userportal',
  application: 'application',
  ldap: 'ldap',
};

export const apiTypeDefaultValue = new Map([
  [GlobalAuthPolicyConsts.userPortal, 'legacy'],
  [GlobalAuthPolicyConsts.application, 'allow'],
  [GlobalAuthPolicyConsts.ldap, 'allow'],
]);

/*
  Factory is used by:
    - SsoApplicationAccessPolicyModel
    - UserPortalFallbackPolicyModel
    - LdapAccessPolicyModel
*/
export default function ConditionalFallbackPolicyModelFactory(apiType) {
  const userPortalFallbackApi = new AuthNFallbackPolicyApi(apiType);
  const defaultValue = apiTypeDefaultValue.get(apiType);
  const model = createModel(
    () => new AuthNFallbackPolicyType({
      resource: apiType,
      effect: {
        action: defaultValue,
      },
    }),
    'id',
    userPortalFallbackApi,
  );

  return {
    namespaced: true,

    state: {
      ...model.state,
    },

    actions: {
      ...model.actions,

      async createItem({ commit, getters }) {
        const result = await userPortalFallbackApi.create(getters.item);
        const policy = new AuthNFallbackPolicyType(result);

        commit('SET_ITEM_STATE', { create: false, update: true });
        commit('SET_ITEM', cloneDeep({ effect: policy.effect }));
        commit('SET_PREVIOUS_ITEM', cloneDeep({ effect: policy.effect }));
      },

      async deleteItem({ commit }) {
        await userPortalFallbackApi.delete();

        const policy = new AuthNFallbackPolicyType({
          effect: { action: defaultValue },
        });

        commit('SET_ITEM_STATE', { create: true, update: false });
        commit('SET_ITEM', cloneDeep({ effect: policy.effect }));
        commit('SET_PREVIOUS_ITEM', cloneDeep({ effect: policy.effect }));
      },

      async fetchItem({ commit }) {
        const policy = await userPortalFallbackApi.fetch();

        if (policy.isDefault) {
          commit('SET_ITEM_STATE', { create: true, update: false });
        } else {
          commit('SET_ITEM_STATE', { create: false, update: true });
        }

        commit('SET_ITEM', cloneDeep({ effect: policy.effect }));
        commit('SET_PREVIOUS_ITEM', cloneDeep({ effect: policy.effect }));
      },

      async saveItem({ dispatch, getters }) {
        const fallbackPolicy = getters.item;
        const originalFallbackPolicy = getters.previousItem;

        if (
          fallbackPolicy.effect.action === defaultValue
          && defaultValue === 'legacy'
          && originalFallbackPolicy.effect.action !== defaultValue) {
          await dispatch('deleteItem');
          return;
        }

        if (getters.isItemBeingCreated === true) {
          await dispatch('createItem');
          return;
        }

        if (getters.isItemBeingUpdated === true) {
          await dispatch('saveChanges');
        }
      },

      async saveChanges({ commit, getters }) {
        const result = await userPortalFallbackApi.update(getters.item);
        const policy = new AuthNFallbackPolicyType(result);

        commit('SET_ITEM_STATE', { create: false, update: true });
        commit('SET_ITEM', cloneDeep({ effect: policy.effect }));
        commit('SET_PREVIOUS_ITEM', cloneDeep({ effect: policy.effect }));
      },
    },

    getters: {
      ...model.getters,
    },

    mutations: {
      ...model.mutations,
    },
  };
}
